/**
 * 登录成功后建立WebSocket连接
 */
import createWebSocket from "@/utils/webSocket/webSocket";
import handleSocketMessage from "@/utils/webSocket/webSocket-mes";

const loginToWebSocket = () => {
  const useWebSocket = new createWebSocket();
  const token = localStorage.getItem('token');
  if (!token) {
    console.error('没有token，无法建立WebSocket连接');
    return false;
  }

  // 心跳保活相关变量
  let heartbeatTimer = null;
  const heartbeatInterval = 30000; // 30秒发送一次心跳
  const heartbeatTimeout = 10000; // 10秒内没收到pong则重连
  let pongTimer = null;
  let reconnectTimer = null;
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;

  // 启动心跳保活
  const startHeartbeat = () => {
    // 清除之前的心跳定时器
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
    }

    heartbeatTimer = setInterval(() => {
      if (useWebSocket.isWebSocketConnected()) {
        // 发送心跳消息
        const heartbeatMsg = {
          type: 'ping',
          timestamp: Date.now()
        };

        useWebSocket.sendMessage(heartbeatMsg).then(success => {
          if (success) {
            // 设置pong超时检测
            if (pongTimer) {
              clearTimeout(pongTimer);
            }
            pongTimer = setTimeout(() => {
              console.warn('心跳响应超时，准备重连');
              handleReconnect();
            }, heartbeatTimeout);
          } else {
            console.warn('心跳发送失败，准备重连');
            handleReconnect();
          }
        });
      } else {
        console.warn('WebSocket未连接，准备重连');
        handleReconnect();
      }
    }, heartbeatInterval);
  };

  // 停止心跳保活
  const stopHeartbeat = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    if (pongTimer) {
      clearTimeout(pongTimer);
      pongTimer = null;
    }
  };

  // 处理重连逻辑
  const handleReconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
    }

    if (reconnectAttempts >= maxReconnectAttempts) {
      console.error('重连次数已达上限，停止重连');
      return;
    }

    reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts - 1), 30000); // 指数退避，最大30秒

    reconnectTimer = setTimeout(() => {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        // 停止当前心跳
        stopHeartbeat();

        // 重新建立连接
        useWebSocket.initWebSocket({
          url: 'gulingtong.cloudgn.com',
          path: `/api/websocket/message/${currentToken}`,
          secure: true
        }).then(() => {
          reconnectAttempts = 0; // 重置重连次数
          startHeartbeat(); // 重新开始心跳
        }).catch(error => {
          console.error('重连失败:', error);
          handleReconnect(); // 继续重连
        });
      }
    }, delay);
  };

  // 根据文档要求，使用wss协议且token在路径中
  useWebSocket.initWebSocket({
    url: 'gulingtong.cloudgn.com',
    path: `/api/websocket/message/${token}`,
    secure: true // 使用wss协议
  }).then(() => {
    startHeartbeat(); // 连接成功后开始心跳
  }).catch(error => {
    console.error('WebSocket连接失败:', error);
    handleReconnect();
  });

  useWebSocket.onMessage((event) => {
    try {
      const data = JSON.parse(event.data);
      // 处理心跳响应
      if (data.type === 'pong') {
        if (pongTimer) {
          clearTimeout(pongTimer);
          pongTimer = null;
        }
        return;
      }
      // 处理其他业务消息
      handleSocketMessage(data);
    } catch {
      // 如果不是JSON格式，直接输出
      handleSocketMessage(event.data);
    }
  });

  // 添加连接错误处理
  useWebSocket.onError((error) => {
    console.error('WebSocket连接错误:', error);
    stopHeartbeat(); // 停止心跳

    // 如果是403错误，处理token过期
    if (error && (error.toString().includes('403') || error.toString().includes('Unexpected response code: 403'))) {
      console.error('WebSocket认证失败，token可能过期或无效');
      localStorage.removeItem('token');
    } else {
      console.error('WebSocket连接错误，尝试重连...');
      handleReconnect();
    }
  });

  // 页面卸载时清理资源
  const cleanup = () => {
    stopHeartbeat();
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
    }
    useWebSocket.closeWebSocket();
  };

  // 监听页面卸载事件
  window.addEventListener('beforeunload', cleanup);
  window.addEventListener('unload', cleanup);

  // 返回清理函数，供外部调用
  return cleanup;
};

export default loginToWebSocket;
